class DripModelViewer {
  static modelCache = new Map();

  constructor(containerId, modelUrl) {
    this.container = document.getElementById(containerId);

    // Get Shopify asset base URL from data attribute (e.g., "https://cdn.shopify.com/.../assets/")
    // Fallback to S3 if not available
    this.assetBaseUrl =
      this.container?.dataset?.assetBaseUrl ||
      "https://spicydan-build-a-drip.s3.us-east-1.amazonaws.com/";

    if (DEBUG_MODE)
      console.log("[DripModelViewer] Using asset base URL:", this.assetBaseUrl);

    // If modelUrl is just a filename (e.g., "BLACK+ONYX.glb"), prepend asset URL
    // If it's already a full URL, use as-is
    // Note: Filenames are already URL-encoded in metafields
    if (modelUrl && !modelUrl.startsWith("http")) {
      modelUrl = this.assetBaseUrl + modelUrl;
    }

    this.modelUrl = modelUrl || this.assetBaseUrl + "v12.glb";
    this.defaultModelUrl = this.modelUrl; // remember original default for fallback

    if (DEBUG_MODE)
      console.log("[DripModelViewer] Default model URL:", this.defaultModelUrl);

    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.model = null;
    this.modelLoaded = false;
    this._currentLoadToken = 0; // used to cancel stale loads
    this.defaultBaseModel = null; // stored original default full model scene clone
    this._accessoryNameCache = new Set(); // names of carabiner/charm nodes merged into current model
    this._lightingInitialized = false; // ensure lights/env added only once
    this.environmentTexture = null; // cache environment map texture

    this.init();

    // Observe both parent and window resize
    const parent = this.container?.parentElement;
    if (parent) {
      new ResizeObserver(() => this.handleResize()).observe(parent);
    }

    window.addEventListener("resize", () => this.handleResize());
  }

  init() {
    THREE.Cache.enabled = true;
    this.scene = new THREE.Scene();

    const aspect = this.container.clientWidth / this.container.clientHeight;
    this.camera = new THREE.PerspectiveCamera(50, aspect, 0.1, 1000);
    this.camera.position.z = 10;

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setSize(
      this.container.clientWidth,
      this.container.clientHeight
    );
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    this.renderer.shadowMap.enabled = true;
    this.scene.background = null;
    this.container.appendChild(this.renderer.domElement);

    this.resizeRendererToContainer();
    // Initialize OrbitControls once
    this.controls = new THREE.OrbitControls(
      this.camera,
      this.renderer.domElement
    );
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.enableZoom = true;
    this.controls.minDistance = 3;
    this.controls.maxDistance = 20;
    this.controls.rotateSpeed = 0.9;
    this.controls.zoomSpeed = 0.7;
    this.controls.panSpeed = 0.25;
    this.loadModel();
    this.animate();
  }

  resizeRendererToContainer() {
    this.resizeContainer();

    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.renderer.setSize(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  resizeContainer() {
    const containerEl = this.container;
    const parent = containerEl?.parentElement;

    if (!containerEl || !parent) return;

    const parentWidth = parent.clientWidth;
    const parentHeight = parent.clientHeight;

    const aspectRatio = 5 / 6;

    // Start by assuming we use full width
    let width = parentWidth;
    let height = width / aspectRatio;

    // If that height is too tall, base it on height instead
    if (height > parentHeight) {
      height = parentHeight;
      width = height * aspectRatio;
    }

    containerEl.style.width = `${width}px`;
    containerEl.style.height = `${height}px`;
  }

  handleResize() {
    this.resizeRendererToContainer();

    const mobileContainer = document.querySelector(
      ".build-a-drip-mobile-options"
    );
    const isMobile = window.innerWidth < 1025;

    if (isMobile && this.modelLoaded && mobileContainer) {
      mobileContainer.classList.remove("hidden");
    }
  }

  loadModel() {
    this.toggleLoader(true);

    if (DripModelViewer.modelCache.has(this.modelUrl)) {
      const cached = DripModelViewer.modelCache.get(this.modelUrl);

      const clonedScene = cached.scene.clone(true);
      this.setupModel({ ...cached, scene: clonedScene });
      this.toggleLoader(false);
      return;
    }

    const dracoLoader = new THREE.DRACOLoader();
    dracoLoader.setDecoderPath(
      "https://cdn.jsdelivr.net/npm/three@0.137.0/examples/js/libs/draco/"
    );

    const loader = new THREE.GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    const originalLoad = THREE.FileLoader.prototype.load;

    THREE.FileLoader.prototype.load = function (
      url,
      onLoad,
      onProgress,
      onError
    ) {
      this.setRequestHeader({}); // Clear any inherited cache-busting headers
      return originalLoad.call(this, url, onLoad, onProgress, onError);
    };

    loader.load(
      this.modelUrl,
      (gltf) => {
        DripModelViewer.modelCache.set(this.modelUrl, gltf);

        const clonedScene = gltf.scene.clone(true);
        this.setupModel({ ...gltf, scene: clonedScene });
        this.toggleLoader(false);
      },
      undefined,
      (err) => console.error("Model load error:", err)
    );
  }

  // Public API: load a different model URL dynamically (cached when possible)
  loadModelUrl(newUrl, callback) {
    // If no newUrl provided, revert to default
    let targetUrl = newUrl || this.defaultModelUrl;

    // If targetUrl is just a filename (not a full URL or absolute path), prepend asset base URL
    // Note: Filenames are already URL-encoded in metafields
    if (
      targetUrl &&
      !targetUrl.startsWith("http") &&
      !targetUrl.startsWith("/")
    ) {
      targetUrl = this.assetBaseUrl + targetUrl;
    }

    if (!targetUrl || targetUrl === this.modelUrl) {
      if (callback) callback();
      return;
    }
    this.modelUrl = targetUrl;
    const loadToken = ++this._currentLoadToken;
    this.toggleLoader(true);

    // Dispose previous model (keep shared materials/textures cached by three.js Cache)
    if (this.model) {
      try {
        this.scene.remove(this.model);
      } catch (e) {
        console.warn("Failed removing previous model", e);
      }
      this.model = null;
    }

    // Reset accessory merge cache when preparing to load a new non-default model so accessories can be re-added
    if (this.defaultModelUrl && this.modelUrl !== this.defaultModelUrl) {
      this._accessoryNameCache = new Set();
    }

    const wasDefaultBefore = this.modelUrl === this.defaultModelUrl;
    const finish = (gltf) => {
      if (loadToken !== this._currentLoadToken) return; // stale
      this.setupModel(gltf);
      // Mark swap meta so configurator can decide on camera animation
      this._justSwappedModel = true;
      this._lastSwapWasDefault = wasDefaultBefore;
      this.toggleLoader(false);
      if (callback) callback();
    };

    // Serve from cache if available
    if (DripModelViewer.modelCache.has(this.modelUrl)) {
      const cached = DripModelViewer.modelCache.get(this.modelUrl);
      const cloned = cached.scene.clone(true);
      finish({ ...cached, scene: cloned });
      return;
    }

    const dracoLoader = new THREE.DRACOLoader();
    dracoLoader.setDecoderPath(
      "https://cdn.jsdelivr.net/npm/three@0.137.0/examples/js/libs/draco/"
    );
    const loader = new THREE.GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    loader.load(
      this.modelUrl,
      (gltf) => {
        DripModelViewer.modelCache.set(this.modelUrl, gltf);
        const cloned = gltf.scene.clone(true);
        finish({ ...gltf, scene: cloned });
      },
      undefined,
      (err) => {
        console.error("Model load error:", err);
        this.toggleLoader(false);
      }
    );
  }

  // Preload (cache only) without swapping current displayed model
  preloadModel(url) {
    if (!url) return;

    // If url is just a filename, prepend asset base URL
    // Note: Filenames are already URL-encoded in metafields
    if (!url.startsWith("http")) {
      url = this.assetBaseUrl + url;
    }

    if (DripModelViewer.modelCache.has(url)) return;

    const dracoLoader = new THREE.DRACOLoader();
    dracoLoader.setDecoderPath(
      "https://cdn.jsdelivr.net/npm/three@0.137.0/examples/js/libs/draco/"
    );
    const loader = new THREE.GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    loader.load(
      url,
      (gltf) => {
        if (!DripModelViewer.modelCache.has(url)) {
          DripModelViewer.modelCache.set(url, gltf);
          if (DEBUG_MODE) console.log("[DripModelViewer] Preloaded model", url);
        }
      },
      undefined,
      (err) => {
        if (DEBUG_MODE)
          console.warn("[DripModelViewer] Preload failed", url, err);
      }
    );
  }

  // Backwards compatibility alias used in open modal logic
  resize() {
    this.handleResize();
  }

  setupModel(gltf) {
    this.model = gltf.scene;
    this.scene.add(this.model);

    // Preserve a pristine clone of the DEFAULT model (v12.glb) for accessory merging
    // Only set this when loading the actual default model URL
    if (!this.defaultBaseModel && this.modelUrl === this.defaultModelUrl) {
      if (DEBUG_MODE)
        console.log(
          "[DripModelViewer] Setting defaultBaseModel from:",
          this.modelUrl
        );
      this.defaultBaseModel = this.model.clone(true);

      // Log ALL nodes in the default model for debugging
      if (DEBUG_MODE) {
        const allNodeNames = [];
        this.defaultBaseModel.traverse((n) => {
          if (n.name) allNodeNames.push(n.name);
        });
        console.log(
          "[DripModelViewer] ALL nodes in default model:",
          allNodeNames
        );
      }

      // Build cache of accessory node names from default
      this._defaultAccessoryNodes = [];
      this.defaultBaseModel.traverse((n) => {
        if (!n.name) return;
        const lower = n.name.toLowerCase();
        if (
          lower.includes("carabiner") ||
          lower.includes("charm") ||
          lower.includes("spacer")
        ) {
          this._defaultAccessoryNodes.push(n);
        }
      });
      if (DEBUG_MODE)
        console.log(
          "[DripModelViewer] Total accessory nodes found:",
          this._defaultAccessoryNodes.length
        );
    }

    // Initialize lights & environment only once, then just apply env map to new model
    if (!this._lightingInitialized) {
      this.initLightingAndEnvironment();
    } else if (this.environmentTexture || this.scene.environment) {
      const envTex = this.environmentTexture || this.scene.environment;
      this.model.traverse((child) => {
        if (child.isMesh && child.material) {
          child.material.envMap = envTex;
          child.material.needsUpdate = true;
        }
      });
    }

    // Controls already created; ensure constraints intact
    if (this.controls) {
      this.controls.minDistance = 3;
      this.controls.maxDistance = 20;
    }

    const box = new THREE.Box3().setFromObject(this.model);
    const center = box.getCenter(new THREE.Vector3());
    this.model.position.sub(center);
    this.model.position.x += 1.2; // Move right
    this.model.position.y += -1.2; // Move down (reduced from 1)

    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = this.camera.fov * (Math.PI / 180);
    let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
    cameraZ *= 1.1;
    this.camera.position.set(0, 0, cameraZ);

    this.camera.lookAt(0, 0, 0);
    // Limit how far user can pan away from origin (after model recentered)
    this._maxPanDistance = maxDim * 0.6; // adjustable scalar

    // Preserve the very first camera state as the canonical "front" reference
    if (!window.initialCameraState) {
      window.saveCameraState();
    }

    this.renderer.shadowMap.enabled = true;
    if (this.directionalLight) {
      this.directionalLight.castShadow = true;
    }

    this.model.traverse((child) => {
      child.visible = false;
      // console.log("Child name:", child.name);
      // console.log("material name:", child.material?.name);
      if (child.isMesh && child.material) {
        if (
          child.name.includes("NECKLACE_-_Bean") &&
          child.material.name &&
          child.material.name.toLowerCase().includes("bead")
        ) {
          // console.log(
          //   "Found bead material:",
          //   child.material.name.toLowerCase()
          // );
          child.material.envMapIntensity = 1.5;
        } else if (child.name.includes("Charm")) {
          child.material.metalness = 1.1;
          child.material.roughness = 0.1;
          child.material.envMapIntensity = 0.6;
        } else if (
          child.name.includes("NECKLACE_PEARL") &&
          child.material.name &&
          child.material.name.toLowerCase().includes("pearl")
        ) {
          child.material.metalness = 0.3;
          child.material.roughness = 0.3;
          child.material.envMapIntensity = 0.2;
        } else {
          child.material.roughness = 0.1;
          child.material.envMapIntensity = 0.6;
        }
      }
    });

    // If we are NOT on the default model, merge missing accessory nodes from stored default
    if (this.defaultBaseModel && this.modelUrl !== this.defaultModelUrl) {
      if (DEBUG_MODE)
        console.log(
          "[DripModelViewer] Current model is NOT default, merging accessories from default model"
        );
      this.mergeAccessoryNodes();
    } else if (
      !this.defaultBaseModel &&
      this.modelUrl !== this.defaultModelUrl
    ) {
      if (DEBUG_MODE)
        console.warn(
          "[DripModelViewer] WARNING: Loading custom model but defaultBaseModel not yet set! Accessories may be missing."
        );
      if (DEBUG_MODE)
        console.warn(
          "[DripModelViewer] modelUrl:",
          this.modelUrl,
          "defaultModelUrl:",
          this.defaultModelUrl
        );
    }

    // Dispatch event AFTER full setup & accessory merge to ensure nodes exist when listeners run
    window.dispatchEvent(new CustomEvent("model-loaded"));
  }

  // One-time lighting & environment setup
  initLightingAndEnvironment() {
    if (this._lightingInitialized) return;

    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.2);
    this.directionalLight = new THREE.DirectionalLight(0xffffff, 0.4);
    this.directionalLight.position.set(5, 5, 5);
    this.directionalLight.castShadow = true;
    this.directionalLight.shadow.bias = -0.0005;

    this.fillLight = new THREE.DirectionalLight(0xffffff, 0.2);
    this.fillLight.position.set(-3, 2, -3);

    this.topLight = new THREE.DirectionalLight(0xffffff, 0.3);
    this.topLight.position.set(0, 3, 2);

    this.scene.add(
      this.ambientLight,
      this.directionalLight,
      this.fillLight,
      this.topLight
    );

    // Try EXR environment (only once)
    let exrLoader = null;
    try {
      exrLoader = new THREE.EXRLoader();
    } catch (e) {
      exrLoader = null;
    }

    if (!exrLoader) {
      console.warn(
        "EXRLoader unavailable; using fallback cube environment (once)."
      );
      this.loadFallbackEnvironment();
      this._lightingInitialized = true;
      return;
    }

    exrLoader.load(
      "https://cdn.shopify.com/s/files/1/0767/9272/5688/files/forest.exr?v=1743608020",
      (texture) => {
        texture.mapping = THREE.EquirectangularReflectionMapping;
        this.scene.environment = texture;
        this.environmentTexture = texture;
        if (this.model) {
          this.model.traverse((child) => {
            if (child.isMesh && child.material) {
              child.material.envMap = texture;
              child.material.needsUpdate = true;
            }
          });
        }
      },
      undefined,
      (err) => {
        console.warn("EXR environment load failed; fallback to cube.", err);
        this.loadFallbackEnvironment();
      }
    );

    this._lightingInitialized = true;
  }

  // Merge carabiner & charm nodes from default model into current model if missing
  mergeAccessoryNodes() {
    if (!this._defaultAccessoryNodes || !this.model) return;
    const existingNames = new Set();
    this.model.traverse((n) => {
      if (n.name) existingNames.add(n.name);
    });

    this._defaultAccessoryNodes.forEach((srcNode) => {
      if (!srcNode.name) return;
      if (existingNames.has(srcNode.name)) return; // already present in current model
      // Clone shallow hierarchy (deep clone preserves materials)
      const clone = srcNode.clone(true);
      // Hide by default; configurator logic will show needed one
      clone.visible = false;
      this.model.add(clone);
      this._accessoryNameCache.add(srcNode.name);
      if (DEBUG_MODE)
        console.log(
          "[DripModelViewer] Merged accessory node from default model:",
          srcNode.name
        );
    });
  }

  loadFallbackEnvironment() {
    // Fallback to the original cube map if EXRLoader isn't available
    const envMapLoader = new THREE.CubeTextureLoader();
    const envMap = envMapLoader.load([
      "https://threejs.org/examples/textures/cube/Bridge2/posx.jpg",
      "https://threejs.org/examples/textures/cube/Bridge2/negx.jpg",
      "https://threejs.org/examples/textures/cube/Bridge2/posy.jpg",
      "https://threejs.org/examples/textures/cube/Bridge2/negy.jpg",
      "https://threejs.org/examples/textures/cube/Bridge2/posz.jpg",
      "https://threejs.org/examples/textures/cube/Bridge2/negz.jpg",
    ]);

    this.scene.environment = envMap;

    this.model.traverse((child) => {
      if (child.isMesh && child.material) {
        child.material.envMap = envMap;
        child.material.needsUpdate = true;
      }
    });
  }

  animate() {
    requestAnimationFrame(() => this.animate());
    // Clamp pan target to keep model framed
    if (this.controls && this._maxPanDistance) {
      const t = this.controls.target;
      const dist = Math.sqrt(t.x * t.x + t.y * t.y + t.z * t.z);
      if (dist > this._maxPanDistance) {
        const scale = this._maxPanDistance / dist;
        t.set(t.x * scale, t.y * scale, t.z * scale);
        // Also gently pull camera position by same delta to avoid sudden jump
        const cam = this.camera.position;
        cam.set(cam.x * scale, cam.y * scale, cam.z * scale);
      }
    }
    this.renderer.render(this.scene, this.camera);
    this.controls?.update();
  }

  toggleLoader(show) {
    this.modelLoaded = !show;
    const loader = document.getElementById("model-loader");
    const viewerContainer = document.querySelector(".build-a-drip-container");
    const mobileContainer = document.querySelector(
      ".build-a-drip-mobile-options"
    );
    const isMobile = window.innerWidth < 768;

    if (loader && viewerContainer) {
      if (show) {
        loader.classList.remove("hidden");
        viewerContainer.classList.remove("visible");
        viewerContainer.classList.add("hidden");

        if (isMobile && mobileContainer) {
          mobileContainer.classList.add("hidden");
        }
      } else {
        loader.classList.add("hidden");
        viewerContainer.classList.remove("hidden");
        viewerContainer.classList.add("visible");

        if (isMobile && mobileContainer) {
          mobileContainer.classList.remove("hidden");
        }
      }
    }
  }
}
